import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { AdminLayout } from './layouts/AdminLayout';
import { LoginPage } from './pages/LoginPage';
import { AuthCallback } from './pages/AuthCallback';
import { AccessDenied } from './pages/AccessDenied';
import { DashboardPage } from './pages/DashboardPage';
import { StaffPage } from './pages/StaffPage';
import { SanctionsPage } from './pages/SanctionsPage';
import { PlayersPage } from './pages/PlayersPage';
import { ConsolePage } from './pages/ConsolePage';
import { ServerDetailPage } from './pages/ServerDetailPage';
import { BanwordsPage } from './pages/BanwordsPage';
import { WhitelistPage } from './pages/WhitelistPage';
import { GradesPage } from './pages/GradesPage';
import { GradeCosmeticsPage } from './pages/GradeCosmeticsPage';
import { BlocksPage } from './pages/config/BlocksPage';
import { ItemsPage } from './pages/config/ItemsPage';
import { EntitiesPage } from './pages/config/EntitiesPage';
import { CraftsPage } from './pages/config/CraftsPage';
import { LootboxPage } from './pages/config/LootboxPage';
import { JobsPage } from './pages/config/JobsPage';
import { RecubeJobsPage } from './pages/config/RecubeJobsPage';
import { WeaponsPage } from './pages/config/WeaponsPage';
import { OreGenerationPage } from './pages/config/OreGenerationPage';
import { AdminShopPage } from './pages/config/AdminShopPage';
import { FactionsPage } from './pages/config/FactionsPage';
import { WorldsPage } from './pages/config/worlds/WorldsPage';
import { DeployRequestsPage } from './pages/DeployRequestsPage';
import { DeployScope } from './deploy/DeployScope';
import { Dashboard as DeployDashboard } from './deploy/pages/Dashboard';
import { Deploy as DeployLauncher } from './deploy/pages/Deploy';
import { DeployProgress } from './deploy/pages/DeployProgress';
import { PromotionProgress as DeployPromotionProgress } from './deploy/pages/PromotionProgress';
import { Containers as DeployContainers } from './deploy/pages/Containers';
import { Environments as DeployEnvironments } from './deploy/pages/Environments';
import { History as DeployHistory } from './deploy/pages/History';
import { ApiServices as DeployApiServices } from './deploy/pages/ApiServices';
import { ResourcePacks as DeployResourcePacks } from './deploy/pages/ResourcePacks';
import { Database as DeployDatabase } from './deploy/pages/Database';
import { InstanceDetail as DeployInstanceDetail } from './deploy/pages/InstanceDetail';
import { Settings as DeploySettings } from './deploy/pages/Settings';
import { Logs as DeployLogs } from './deploy/pages/Logs';
import { DevEnvDetail as DeployDevEnvDetail } from './deploy/pages/DevEnvDetail';
import { BetaSlotDetail as DeployBetaSlotDetail } from './deploy/pages/BetaSlotDetail';
import { Login as DeployLogin } from './deploy/pages/Login';
import { ProfilerPage as DeployProfilerPage } from './deploy/pages/profiler/ProfilerPage';
import { ProfilerServerPage as DeployProfilerServerPage } from './deploy/pages/profiler/ProfilerServerPage';
import { NetworkDomains as DeployNetworkDomains } from './deploy/pages/network/NetworkDomains';
import { NetworkCertificates as DeployNetworkCertificates } from './deploy/pages/network/NetworkCertificates';
import { DiscordScope } from './discord/DiscordScope';
import { DiscordHome } from './discord/pages/DiscordHome';
import { Status as DiscordStatus } from './discord/pages/Status';
import { AdminPage } from './pages/AdminPage';
import { GlobalSettingsPage } from './pages/GlobalSettingsPage';
import { StatsPage } from './pages/StatsPage';
import { PlayerStatsPage } from './pages/PlayerStatsPage';
import { OnboardingPage } from './pages/stats/OnboardingPage';
import { LogsPage } from './pages/LogsPage';
import { WatchdogPage } from './pages/WatchdogPage';
import { useEffect, useState } from 'react';
import { useAuthStore, Permission, StaffRole } from './stores/auth.store';
import { useProjectPagesStore } from './stores/projectPages.store';
import { usePanelFeaturesStore } from './stores/panelFeatures.store';
import { useSupervisionLanding } from './components/layout/supervisionNav';
import { DASHBOARD_PATH } from './config/panelModules';
import { useSessionStore as useDeploySessionStore } from './deploy/stores/session.store';
import { useDeployEnvStore } from './deploy/stores/deployEnv.store';
import { EmergencyApp, EmergencyReload } from './emergency/EmergencyApp';
import { isEmergencyPath } from './emergency/emergencyPath';
import { isSettingsSection, settingsPath, useSettingsSections, type SettingsSection } from './config/settingsSections';

/**
 * Check if current URL has auth redirect params (from static HTML hack)
 */
function isAuthRedirect(): boolean {
  const params = new URLSearchParams(window.location.search);
  return params.get('_auth_redirect') === '1' && !!params.get('code');
}

/** Redirige /deploiement/environments vers l'env mémorisé (choix partagé entre pages). */
function DeployEnvRedirect() {
  const env = useDeployEnvStore((s) => s.env);
  return <Navigate to={`/deploiement/environments/${env}`} replace />;
}

export function App() {
  // If this is an auth redirect from the static HTML hack, show AuthCallback directly
  if (isAuthRedirect()) {
    return (
      <BrowserRouter>
        <AuthCallback />
      </BrowserRouter>
    );
  }

  // Accès de secours au Déploiement (/secours) : routeur à part, sans rien monter du panel.
  if (isEmergencyPath(window.location.pathname)) {
    return <EmergencyApp />;
  }

  return (
    <BrowserRouter>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/microsoft/recubebe-panel-redirect" element={<AuthCallback />} />
        {/* Accès de secours : atteint seulement par navigation interne, recharge vers EmergencyApp */}
        <Route path="/secours/*" element={<EmergencyReload />} />

        {/* Access Denied - requires auth but shows error */}
        <Route path="/access-denied" element={<AuthenticatedRoute><AccessDenied /></AuthenticatedRoute>} />

        {/* Protected routes - requires auth + panel access permission */}
        <Route path="/" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
          {/* Accueil : le Dashboard, ou la première page servie par le satellite du groupe actif */}
          <Route index element={<SupervisionIndex />} />
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="staff" element={<StaffPage />} />
          <Route path="sanctions" element={<SanctionsPage />} />
          <Route path="players" element={<PlayersPage />} />
          <Route path="players/:uuid" element={<PlayersPage />} />
          <Route path="console" element={<ConsolePage />} />
          <Route path="console/crashs" element={<WatchdogPage />} />
          <Route path="console/:identifier" element={<ServerDetailPage />} />
          {/* Ancien chemin conservé : redirige vers l'onglet « Crashs serveur » de Server */}
          <Route path="crashs" element={<Navigate to="/console/crashs" replace />} />
          <Route path="banwords" element={<BanwordsPage />} />
          <Route path="whitelist" element={<WhitelistPage />} />
          <Route path="stats" element={<StatsPage />} />
          {/* 'players' est un segment STATIQUE : React Router le fait gagner sur ':section',
              donc /stats/players reste la page Joueurs et non une section de StatsPage. */}
          <Route path="stats/players" element={<PlayerStatsPage />} />
          {/* Segment statique lui aussi : Onboarding est une page à part, pas une section de StatsPage. */}
          <Route path="stats/onboarding" element={<OnboardingPage />} />
          <Route path="stats/:section" element={<StatsPage />} />
          {/* Grades in-game (LuckPerms) : un cadre par groupe de jeu */}
          <Route path="grades" element={<GradesPage />} />

          {/* Espace Configuration (in-game / packs) */}
          {/* Pack / Fichiers / Containers (anciens onglets de Configuration > Paramètres) vivent dans
              les Paramètres généraux : les anciennes URL restent des favoris valides et y mènent. */}
          <Route path="config" element={<Navigate to="/parametres/packs" replace />} />
          <Route path="config/files" element={<Navigate to="/parametres/fichiers" replace />} />
          <Route path="config/servers" element={<Navigate to="/parametres/containers" replace />} />
          <Route path="config/blocks" element={<BlocksPage />} />
          <Route path="config/items" element={<ItemsPage />} />
          <Route path="config/entities" element={<EntitiesPage />} />
          <Route path="config/crafts" element={<CraftsPage />} />
          {/* Les grades in-game ont quitté la Configuration pour la Supervision : l'ancienne URL
              reste un favori valide. */}
          <Route path="config/grades" element={<Navigate to="/grades" replace />} />
          {/* Cosmétiques des grades : le catalogue (création des ornements, rank tags, glyphes). Le
              choix de chaque grade se fait dans Supervision > Grades ; ?grade= y redirige. */}
          <Route path="config/cosmetics" element={<GradeCosmeticsPage />} />
          <Route path="config/lootbox" element={<LootboxPage />} />
          <Route path="config/jobs" element={<JobsPage />} />
          {/* RecubeJobsMod : autre mod de métiers, même nom de fichier que JobsMod, page à part. */}
          <Route path="config/recubejobs" element={<RecubeJobsPage />} />
          {/* Armes de RecubeICBM (recubeicbm.config.json) : dégâts, cadence, chargeurs, règles communes. */}
          <Route path="config/weapons" element={<WeaponsPage />} />
          {/* Génération de minerais et régénération automatique de WorldOriginMod (worldorigin.config.json, clés Ores et AutoRegen). */}
          <Route path="config/minerais" element={<OreGenerationPage />} />
          <Route path="config/adminshop" element={<AdminShopPage />} />
          <Route path="config/factions" element={<FactionsPage />} />
          <Route path="config/worlds" element={<WorldsPage />} />
          {/* Valider les demandes d'édition depuis l'espace Configuration : le droit
              « config.requests » doit être exploitable sans ouvrir tout l'espace Déploiement,
              qui reste réservé aux admins. Même page que /deploiement/requests. */}
          <Route path="config/requests" element={<DeployRequestsPage />} />
          {/* Catégorie Discord (gestion du bot support) — ADMIN ONLY, dans l'espace Configuration */}
          <Route path="config/discord" element={<AdminRoute><DiscordScope /></AdminRoute>}>
            <Route index element={<Navigate to="/config/discord/bot" replace />} />
            <Route path="bot" element={<DiscordHome variant="bot" />} />
            <Route path="exploitation" element={<DiscordHome variant="ops" />} />
            <Route path="exploitation/status" element={<DiscordStatus />} />
          </Route>
          <Route path="logs" element={<LogsPage />} />

          {/* Espace Déploiement : produit complet repris du front CI/CD (scopé .deploy-scope) — ADMIN ONLY */}
          <Route path="deploiement" element={<AdminRoute><DeployScope /></AdminRoute>}>
            {/* Login séparé recube-cicd (JWT distinct du token Xbox) */}
            <Route path="login" element={<DeployLogin />} />
            <Route index element={<DeployAuthGate><DeployDashboard /></DeployAuthGate>} />
            <Route path="deploy" element={<DeployAuthGate><DeployLauncher /></DeployAuthGate>} />
            <Route path="deploy/progress" element={<DeployAuthGate><DeployProgress /></DeployAuthGate>} />
            <Route path="deployments" element={<DeployAuthGate><DeployPromotionProgress /></DeployAuthGate>} />
            <Route path="containers" element={<DeployAuthGate><DeployContainers /></DeployAuthGate>} />
            <Route path="containers/:code" element={<DeployAuthGate><DeployContainers /></DeployAuthGate>} />
            <Route path="environments" element={<DeployEnvRedirect />} />
            <Route path="environments/:envId" element={<DeployAuthGate><DeployEnvironments /></DeployAuthGate>} />
            <Route path="environments/dev/:envId" element={<DeployAuthGate><DeployDevEnvDetail /></DeployAuthGate>} />
            <Route path="environments/:envId/instance/:identifier" element={<DeployAuthGate><DeployInstanceDetail /></DeployAuthGate>} />
            <Route path="environments/beta/:slotId" element={<DeployAuthGate><DeployBetaSlotDetail /></DeployAuthGate>} />
            <Route path="history" element={<DeployAuthGate><DeployHistory /></DeployAuthGate>} />
            <Route path="apis" element={<DeployAuthGate><DeployApiServices /></DeployAuthGate>} />
            <Route path="resource-packs" element={<DeployAuthGate><DeployResourcePacks /></DeployAuthGate>} />
            <Route path="database" element={<DeployAuthGate><DeployDatabase /></DeployAuthGate>} />
            <Route path="logs" element={<DeployAuthGate><DeployLogs /></DeployAuthGate>} />
            <Route path="settings" element={<DeployAuthGate><DeploySettings /></DeployAuthGate>} />
            {/* Profileur de tick des serveurs de jeu. Ses mesures viennent du satellite du projet, par
                la passerelle du panel (session du panel) : la page ne passe donc pas par la connexion
                au CICD, et reste lisible quand celui-ci ne répond pas. */}
            <Route path="profileur" element={<DeployProfilerPage />} />
            <Route path="profileur/:serverKey" element={<DeployProfilerServerPage />} />
            {/* Réseau du projet actif : vhosts du reverse proxy et certificats (via le CICD → ssl-manager) */}
            <Route path="reseau" element={<Navigate to="/deploiement/reseau/domaines" replace />} />
            <Route path="reseau/domaines" element={<DeployAuthGate><DeployNetworkDomains /></DeployAuthGate>} />
            <Route path="reseau/certificats" element={<DeployAuthGate><DeployNetworkCertificates /></DeployAuthGate>} />
            {/* Ancien flux « demandes d'édition » conservé, désormais sous-onglet de l'espace */}
            <Route path="requests" element={<DeployAuthGate><DeployRequestsPage /></DeployAuthGate>} />
            {/* Route inconnue sous /deploiement : rester DANS l'espace */}
            <Route path="*" element={<Navigate to="/deploiement" replace />} />
          </Route>

          {/* Administration (users & grades) - gardé côté nav pour les admins */}
          <Route path="admin" element={<AdminPage />} />

          {/* Paramètres généraux (écrou du header) : fonctionnalités, admins, grades, projets, grades
              protégés (ADMIN ONLY), et Pack / Fichiers / Containers du projet actif, chacun sous son
              droit de grade (config/settingsSections). Dissociés de Grades & accès. */}
          <Route path="parametres" element={<SettingsIndex />} />
          <Route path="parametres/:section" element={<SettingsRoute><GlobalSettingsPage /></SettingsRoute>} />

          {/* Route inconnue mais authentifiée : rester dans le layout (pas de démontage d'AdminLayout),
              sur l'accueil */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>

        {/* Catch all (hors layout : non authentifié, etc.) */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

/**
 * Route that only requires authentication (no permission check)
 * Used for AccessDenied page
 */
function AuthenticatedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

/**
 * Route réservée aux administrateurs (ex. espace Déploiement).
 * Redirige vers /access-denied si l'utilisateur n'est pas admin.
 */
function AdminRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const user = useAuthStore((state) => state.user);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const isAdmin = !!user?.isAdmin || user?.role === StaffRole.ADMIN;
  if (!isAdmin) {
    return <Navigate to="/access-denied" replace />;
  }

  return <>{children}</>;
}

/** Attente maximale des réglages des modules avant de choisir l'accueil quand même. */
const LANDING_MODULES_WAIT_MS = 4000;

/**
 * Accueil du panel (`/`, connexion, route inconnue) : le Dashboard, comme avant. Sous un groupe de
 * jeu dont le satellite ne sert aucune carte du Dashboard (groupe « grades seuls »), la première
 * page ouverte de la Supervision (cf useSupervisionLanding). Ce choix-là lit les modules du projet :
 * il attend leurs réglages, au plus quelques secondes, pour ne pas mener à un module coupé. Le
 * layout a déjà attendu les groupes de jeu (la route `/` lit le groupe).
 */
function SupervisionIndex() {
  const landing = useSupervisionLanding();
  // Deux lectures sans condition : un `&&` entre les deux hooks sauterait le second tant que les
  // pages ne sont pas prêtes, et le nombre de hooks changerait d'un rendu à l'autre.
  const pagesReady = useProjectPagesStore((s) => s.ready);
  const featuresLoaded = usePanelFeaturesStore((s) => s.loaded);
  const modulesReady = pagesReady && featuresLoaded;
  const [waitExpired, setWaitExpired] = useState(false);
  const waiting = landing !== DASHBOARD_PATH && !modulesReady && !waitExpired;
  useEffect(() => {
    if (!waiting) return;
    const t = window.setTimeout(() => setWaitExpired(true), LANDING_MODULES_WAIT_MS);
    return () => window.clearTimeout(t);
  }, [waiting]);
  if (waiting) return <div style={{ padding: 24 }}>Chargement…</div>;
  return <Navigate to={landing} replace />;
}

/**
 * Accueil des Paramètres généraux : la première section ouverte à l'utilisateur (Fonctionnalités
 * pour un admin global, comme avant ; Pack, Fichiers ou Containers pour un grade qui n'a que ça).
 */
function SettingsIndex() {
  const sections = useSettingsSections();
  return <Navigate to={settingsFallbackPath(sections)} replace />;
}

/**
 * Cible quand la section demandée est fermée : la première ouverte, sinon l'accueil. Jamais
 * /access-denied : l'utilisateur est déjà dans le panel (ProtectedRoute l'a admis), et cet écran
 * hors layout, sans sélecteur de projet, le laisserait sans moyen de revenir au projet d'avant.
 */
function settingsFallbackPath(sections: SettingsSection[]): string {
  return sections.length > 0 ? settingsPath(sections[0]!) : '/';
}

/**
 * Garde d'une section des Paramètres généraux. Les réglages du panel restent réservés aux admins
 * globaux ; Pack, Fichiers et Containers suivent le droit de grade qui les ouvrait dans
 * Configuration. Section fermée (URL tapée, changement de projet) : la première section ouverte,
 * comme le faisaient les anciens onglets ; aucune : l'accueil, dans le layout.
 */
function SettingsRoute({ children }: { children: React.ReactNode }) {
  const { section } = useParams();
  const sections = useSettingsSections();
  if (!isSettingsSection(section)) return <Navigate to="/parametres" replace />;
  if (sections.includes(section)) return <>{children}</>;
  return <Navigate to={settingsFallbackPath(sections)} replace />;
}

/**
 * Gate d'authentification pour les pages internes du Déploiement.
 * Vérifie qu'un `recube.token` est en session (via useSessionStore) — sinon
 * redirige vers /deploiement/login pour le login séparé JWT recube-cicd.
 */
function DeployAuthGate({ children }: { children: React.ReactNode }) {
  const isLoggedIn = useDeploySessionStore((s) => s.isLoggedIn);
  const bootstrapping = useDeploySessionStore((s) => s.bootstrapping);
  const bootstrap = useDeploySessionStore((s) => s.bootstrap);

  useEffect(() => {
    if (bootstrapping) void bootstrap();
  }, [bootstrapping, bootstrap]);

  if (bootstrapping) {
    return <div style={{ padding: 24 }}>Chargement…</div>;
  }
  if (!isLoggedIn) {
    return <Navigate to="/deploiement/login" replace />;
  }
  return <>{children}</>;
}

/**
 * Route that requires authentication AND panel access permission
 * Redirects to AccessDenied if user doesn't have VIEW_DASHBOARD permission
 */
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const user = useAuthStore((state) => state.user);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check if user has panel access
  const hasAccess = user?.permissions?.includes(Permission.VIEW_DASHBOARD) ?? false;
  if (!hasAccess) {
    return <Navigate to="/access-denied" replace />;
  }

  return <>{children}</>;
}
